import React, { useState, useEffect, useRef, useMemo } from 'react';
import useAppStore from '../store'; 
import { useCalculationsSelector } from '../store/selectors/calculations';

const calculatorStyles = `
  @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;700&display=swap');

  /* PE Tokens — Light/Dark folgen html.dark automatisch */
  .sys-calc {
    --sc-bg: var(--pe-bg-primary);
    --sc-surface: var(--pe-bg-card);
    --sc-elevated: var(--pe-bg-elevated);
    --sc-panel: var(--pe-bg-card);
    --sc-keypad: var(--pe-bg-sunken);
    --sc-nav: var(--pe-bg-elevated);
    --sc-input: var(--pe-bg-input);
    --sc-input-disabled: var(--pe-bg-elevated);
    --sc-border: var(--pe-border-subtle);
    --sc-border-strong: var(--pe-border-strong);
    --sc-text: var(--pe-text-primary);
    --sc-muted: var(--pe-text-muted);
    --sc-accent: var(--pe-accent);
    --sc-accent-text: var(--pe-accent-text);
    color: var(--sc-text);
    font-family: ui-sans-serif, system-ui, sans-serif;
  }

  /* Shell: echte Carbon-Feinfaser (Dark) / Warm-Platin flach (Light) — kein Kreis-/Golfball-Muster */
  .sys-calc-shell {
    background-color: var(--pe-bg-card);
    background-image: none;
    border: 1px solid var(--pe-border-subtle);
    box-shadow: 0 20px 40px -16px rgba(28,25,23,0.28), inset 0 1px 0 rgba(255,255,255,0.7);
  }
  html.dark .sys-calc-shell {
    background-color: #0b0b0b;
    background-image: var(--pe-carbon-fiber);
    background-size: auto;
    background-repeat: repeat;
    background-position: 0 0;
    border-color: #2a2a2a;
    box-shadow: 0 30px 60px -15px rgba(0,0,0,0.85), inset 0 1px 1px rgba(255,255,255,0.05);
  }

  .sys-calc-header {
    background-color: var(--pe-bg-elevated);
    background-image: none;
    border-bottom: 1px solid var(--pe-border-subtle);
  }
  /* Carbon sichtbar im Header — kein opaker Verlauf darüber */
  html.dark .sys-calc-header {
    background-color: #0b0b0b !important;
    background-image: var(--pe-carbon-fiber) !important;
    background-size: auto !important;
    background-repeat: repeat !important;
    background-position: 0 0 !important;
    border-bottom-color: rgba(255,255,255,0.08);
    box-shadow: inset 0 -1px 0 rgba(0,0,0,0.55);
  }

  .sys-calc-body { background: var(--sc-bg); }
  html.dark .sys-calc-body { background: #08090b; }

  .sys-calc-panel {
    background: var(--sc-panel);
    border: 1px solid var(--sc-border);
  }
  .sys-calc-elevated { background: var(--sc-elevated); }
  .sys-calc-keypad { background: var(--sc-keypad); }
  .sys-calc-nav {
    background: var(--sc-nav);
    background-image: none;
    border-top: 1px solid var(--sc-border);
  }
  html.dark .sys-calc-nav {
    background-color: #0b0b0b !important;
    background-image: var(--pe-carbon-fiber) !important;
    background-size: auto !important;
    background-repeat: repeat !important;
    background-position: 0 0 !important;
  }

  .sys-calc-chip {
    background: var(--pe-bg-sunken);
    border: 1px solid var(--pe-border-subtle);
    color: var(--sc-accent-text);
  }
  html.dark .sys-calc-chip {
    background: rgba(0,0,0,0.45);
    border-color: rgba(255,255,255,0.1);
  }

  .sys-calc-text { color: var(--sc-text); }
  .sys-calc-muted { color: var(--sc-muted); }
  .sys-calc-accent { color: var(--sc-accent-text); }

  /* Kennzahlen / Kacheln — lesbar in Light & Dark */
  .sys-calc-metric {
    background: var(--pe-bg-sunken);
    border: 1px solid var(--sc-border);
    border-radius: 0.65rem;
    padding: 0.75rem 0.6rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    text-align: center;
    gap: 4px;
    min-height: 92px;
  }
  .sys-calc-metric-label {
    font-size: 12px;
    line-height: 1.25;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    color: var(--sc-text);
  }
  .sys-calc-metric-value {
    font-size: 1.35rem;
    line-height: 1.15;
    font-family: ui-monospace, monospace;
    font-weight: 900;
    color: var(--sc-accent-text);
  }
  .sys-calc-metric-value-plain {
    font-size: 1.35rem;
    line-height: 1.15;
    font-family: ui-monospace, monospace;
    font-weight: 900;
    color: var(--sc-text);
  }
  .sys-calc-metric-sub {
    font-size: 12px;
    font-weight: 700;
    color: var(--sc-muted);
    line-height: 1.25;
  }
  .sys-calc-factor-bar {
    background: var(--pe-bg-sunken);
    border: 1px solid var(--sc-border);
    border-radius: 0.5rem;
  }
  .sys-calc-factor-bar .sys-calc-metric-value-plain {
    font-size: 14px;
  }
  .sys-calc-need-bar {
    background: var(--pe-bg-elevated);
    border: 1px solid var(--sc-border-strong);
    border-radius: 0.5rem;
  }
  .sys-calc-step-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border-radius: 999px;
    background: var(--sc-accent);
    color: #0b0c10;
    font-size: 10px;
    font-weight: 900;
    flex-shrink: 0;
  }
  .sys-calc-tile-reach {
    background: #dbeafe;
    border: 1px solid #93c5fd;
  }
  .sys-calc-tile-reach .sys-calc-metric-label { color: #1d4ed8; }
  .sys-calc-tile-reach .sys-calc-metric-value { color: #1e3a8a; }
  .sys-calc-tile-reach .sys-calc-metric-sub { color: #1e40af; }
  html.dark .sys-calc-tile-reach {
    background: rgba(30, 58, 138, 0.25);
    border-color: rgba(59, 130, 246, 0.35);
  }
  html.dark .sys-calc-tile-reach .sys-calc-metric-label { color: #93c5fd; }
  html.dark .sys-calc-tile-reach .sys-calc-metric-value { color: #dbeafe; }
  html.dark .sys-calc-tile-reach .sys-calc-metric-sub { color: rgba(147, 197, 253, 0.7); }
  .sys-calc-tile-ok {
    background: #dcfce7;
    border: 1px solid #86efac;
  }
  .sys-calc-tile-ok .sys-calc-metric-value { color: #166534; }
  html.dark .sys-calc-tile-ok {
    background: rgba(20, 83, 45, 0.25);
    border-color: rgba(34, 197, 94, 0.3);
  }
  html.dark .sys-calc-tile-ok .sys-calc-metric-value { color: #4ade80; }
  .sys-calc-tile-bad {
    background: #fee2e2;
    border: 1px solid #fca5a5;
  }
  .sys-calc-tile-bad .sys-calc-metric-value { color: #b91c1c; }
  html.dark .sys-calc-tile-bad {
    background: rgba(127, 29, 29, 0.25);
    border-color: rgba(239, 68, 68, 0.3);
  }
  html.dark .sys-calc-tile-bad .sys-calc-metric-value { color: #f87171; }
  .sys-calc-stepper-btn {
    flex: 1;
    background: color-mix(in srgb, var(--sc-text) 8%, transparent);
    border-radius: 2px;
    font-size: 10px;
    color: var(--sc-accent-text);
  }
  .sys-calc-stepper-btn:hover {
    background: color-mix(in srgb, var(--sc-text) 14%, transparent);
  }
  .sys-calc-divider { border-color: var(--sc-border); }

  .sys-calc-inset {
    background: var(--pe-bg-sunken);
    border: 1px solid var(--pe-border-subtle);
  }
  .sys-calc-track {
    background: var(--pe-bg-input);
    border: 1px solid var(--pe-border-subtle);
  }
  .sys-calc-track-active {
    background: var(--pe-bg-elevated);
    color: var(--pe-accent-text);
  }
  .sys-calc-step {
    background: var(--pe-bg-hover);
    color: var(--pe-accent-text);
  }
  .sys-calc-readout {
    background: var(--pe-bg-input);
    border: 1px solid var(--pe-border-strong);
    color: var(--pe-accent-text);
  }
  .sys-calc-select {
    background: var(--pe-bg-input);
    border: 1px solid var(--pe-border-strong);
    color: var(--pe-text-primary);
  }
  .sys-calc-result {
    background: var(--pe-bg-sunken);
    border: 1px solid color-mix(in srgb, var(--pe-accent) 28%, transparent);
  }

  .noble-input {
    background-color: var(--sc-input);
    border: 1px solid var(--sc-border-strong);
    color: var(--sc-text);
    padding: 10px 12px;
    border-radius: 8px;
    width: 100%;
    font-family: 'JetBrains Mono', monospace;
    font-size: 16px;
    outline: none;
    transition: border-color 0.2s;
  }
  .noble-input:disabled {
    opacity: 1;
    color: var(--sc-accent-text);
    border-color: var(--sc-border);
    cursor: default!important;
    background-color: var(--sc-input-disabled);
    font-weight: bold;
  }
  .noble-input.has-stepper { padding-right: 48px; }
  .noble-input:focus { border-color: var(--sc-accent); }

  input[type=number]::-webkit-inner-spin-button,
  input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }

  .calc-gradient {
    background: linear-gradient(180deg, var(--pe-bg-card) 0%, var(--pe-bg-sunken) 100%);
  }
  html.dark .calc-gradient {
    background: linear-gradient(180deg, #2d313a 0%, #1a1c24 100%);
  }
  .btn-shadow { box-shadow: inset 0 1px 0 rgba(255,255,255,0.55), 0 1px 2px rgba(28,25,23,0.12); }
  html.dark .btn-shadow { box-shadow: inset 0 1px 0 rgba(255,255,255,0.05), 0 2px 4px rgba(0,0,0,0.3); }

  .unit-label {
    pointer-events: none;
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--sc-muted);
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
  }
  .unit-label.with-stepper { right: 40px; }

  .custom-scrollbar::-webkit-scrollbar { width: 4px; }
  .custom-scrollbar::-webkit-scrollbar-track { background: color-mix(in srgb, var(--pe-text-primary) 6%, transparent); }
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: color-mix(in srgb, var(--pe-accent-text) 50%, transparent);
    border-radius: 10px;
    border: 1px solid color-mix(in srgb, var(--pe-text-primary) 12%, transparent);
  }

  .toggle-icon { transition: transform 0.3s ease; }
  .equal-glow { box-shadow: 0 0 10px color-mix(in srgb, var(--pe-accent) 22%, transparent), inset 0 0 6px color-mix(in srgb, var(--pe-accent) 10%, transparent); }

  .noble-checkbox {
    appearance: none;
    background-color: var(--sc-border-strong);
    margin: 0;
    font: inherit;
    color: currentColor;
    width: 1.15em;
    height: 1.15em;
    border: 1px solid currentColor;
    border-radius: .15em;
    display: grid;
    place-content: center;
    cursor: pointer!important;
  }
  .noble-checkbox::before {
    content: "";
    width: .65em;
    height: .65em;
    transform: scale(0);
    transition: 120ms transform ease-in-out;
    box-shadow: inset 1em 1em var(--sc-accent);
    background-color: var(--sc-accent);
    transform-origin: center;
    clip-path: polygon(14% 44%, 0 65%, 50% 100%, 100% 16%, 80% 0%, 43% 62%);
  }
  .noble-checkbox:checked::before { transform: scale(1); }
  .noble-checkbox:checked { border-color: var(--sc-accent); }

  .nav-icon-3d {
    filter: drop-shadow(0 1px 1px rgba(28,25,23,0.18));
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  html.dark .nav-icon-3d { filter: drop-shadow(0 2px 3px rgba(0,0,0,0.5)); }
  .nav-button-active .nav-icon-3d {
    filter: drop-shadow(0 0 6px color-mix(in srgb, var(--pe-accent) 40%, transparent));
    transform: translateY(-2px);
  }
  .nav-text-shadow { text-shadow: none; }
  html.dark .nav-text-shadow { text-shadow: 0 1px 2px rgba(0,0,0,0.8); }

  .switcher-track {
    background-color: var(--sc-input);
    border: 1px solid var(--sc-border-strong);
    border-radius: 10px;
    padding: 2px;
    display: flex;
    align-items: center;
    cursor: pointer;
    transition: border-color 0.2s;
  }
  .switcher-track:hover { border-color: var(--sc-accent); }
  .switcher-item {
    flex: 1;
    text-align: center;
    padding: 6px 2px;
    font-size: 10px;
    font-weight: 800;
    text-transform: uppercase;
    border-radius: 7px;
    transition: all 0.2s;
  }
  .switcher-item.active {
    background-color: var(--sc-surface);
    color: var(--sc-accent-text);
    box-shadow: 0 1px 4px rgba(28,25,23,0.12);
  }
  html.dark .switcher-item.active { box-shadow: 0 2px 10px rgba(0,0,0,0.5); }
  .switcher-item.inactive { color: var(--sc-muted); }

  /* Breite: Felder/Kennzahlen in 1–2 Zeilen, keine Panel-Spalten */
  .sys-calc-wide .sys-calc-metrics {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
  .sys-calc-wide .sys-calc-fields-3 {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  .sys-calc-wide .sys-calc-inline-result {
    display: grid;
    grid-template-columns: 1fr 1fr auto;
    gap: 0.5rem;
    align-items: end;
  }
  .sys-calc-wide .sys-calc-key-btn {
    min-height: 3.25rem;
    font-size: 1.35rem;
  }
`;

interface Props {
  onClose: () => void;
  onDragStart: (e: React.MouseEvent) => void;
  position: { x: number; y: number };
}

const CALC_WIDTH_MIN = 360;
const CALC_WIDTH_DEFAULT = 390;
const CALC_WIDTH_PROFIT = 800;
const CALC_WIDTH_TOOLS = 640;
const CALC_WIDTH_MAX = 920;

export default function SystemCalculator({ onClose, onDragStart, position }: Props) {
  const calcs = useCalculationsSelector();
  const { settings, clientData } = useAppStore();
  
  const currency = calcs.currencyCode || 'EUR';
  const vatRate = calcs.mwstSatz;
  
  const kalkStateData = clientData.kalkulator || {};
  const barometerSoll = Number(kalkStateData.barometerStundenfaktor ?? 0);
  const useEchterStundensatz = kalkStateData.useEchterStundensatz || false;
  const echterStundensatz = kalkStateData.echterStundensatz || 0;
  
  const STORE_HOURLY_RATE = (useEchterStundensatz && echterStundensatz > 0) ? echterStundensatz : barometerSoll;
  const STORE_FACTOR_TARGET = Math.round((calcs.lohnfaktorAktiv || 3.89) * 100) / 100;
  const STORE_FACTOR_BREAKEVEN = Math.round(((calcs.lohnfaktorData as any)?.kostendeckungsfaktor2 || 3.22) * 100) / 100;
  
  const WPM = calcs.wochensatz || 4.33;
  const prodFactor = (settings.global?.Auslastungsfaktor || 75) / 100;
  const pufferFactor = (settings.global?.Urlaubsausgleich || 20) / 100;
  const MONTHLY_TURNOVER_BRUTTO = calcs.monatsumsatzBrutto;
  const bwaPeriod = settings.global?.BwaMonate || 12;

  const fmt = (n: number) => new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
  const fmt0 = (n: number) => new Intl.NumberFormat('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);

  const [panelWidth, setPanelWidth] = useState(CALC_WIDTH_DEFAULT);
  const isResizing = useRef(false);

  const [activeTab, setActiveTab] = useState<'calc' | 'profit' | 'hr' | 'biz'>('calc');
  const [zoomIndex, setZoomIndex] = useState(0);
  const zoomLevels = [1, 1.15, 1.3];
  const isWide = panelWidth >= 520;

  const [formula, setFormula] = useState("");
  const [calcHistory, setCalcHistory] = useState<{ f: string; r: number }[]>([]);
  const [isGross, setIsGross] = useState(false);
  const [isYear, setIsYear] = useState(false);
  const tapeRef = useRef<HTMLDivElement>(null);

  const evalForm = (f: string): number => {
    if (!f) return 0;
    // eslint-disable-next-line no-new-func
    try { return new Function('return ' + f.replace(/,/g, '.'))() || 0; } catch { return 0; }
  };

  /** Kurze Zahl für Weiterrechnen — kein 1166.6666666666667 */
  const cleanNum = (n: number) => {
    if (!Number.isFinite(n)) return '0';
    return String(Math.round(n * 1e10) / 1e10);
  };
  const prettyFormula = (f: string) =>
    f.replace(/\*/g, '×').replace(/\//g, '÷').replace(/(\d+\.\d{4,})/g, (m) => {
      const n = Number(m);
      return Number.isFinite(n) ? cleanNum(n) : m;
    });

  const handleCalcInput = (c: string) => {
    if (c === 'AC') { setFormula(""); setCalcHistory([]); }
    else if (c === 'DEL') { setFormula(prev => prev.slice(0, -1)); }
    else if (c === 'PM') { if (formula) setFormula(cleanNum(evalForm(formula) * -1)); }
    else if (c === '%') { setFormula((prev) => cleanNum((parseFloat(prev) || 0) / 100)); }
    else if (c === '=') {
      if (formula) {
        const r = evalForm(formula);
        setCalcHistory(prev => [...prev, { f: prettyFormula(formula), r }]);
        setFormula(cleanNum(r));
      }
    }
    else { setFormula(prev => prev + c); }
  };

  // --- UX LOGIK: SELECT ALL & ENTER KEY ---
  const [activeField, setActiveField] = useState<string | null>(null);
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>, id: string) => {
    setActiveField(id);
    e.target.select();
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') e.currentTarget.blur();
  };
  const handleBlur = () => setActiveField(null);
  // ----------------------------------------

  const [pMode, setPMode] = useState<'total' | 'hourly'>('hourly');
  const [pWage, setPWage] = useState(2563.36);
  const [pHourly, setPHourly] = useState(16.00);
  const [pWorkHours, setPWorkHours] = useState(37.0);
  const [pDays, setPDays] = useState(5);
  /** Wie im Excel-Original: Standardmäßig aktiv, damit Urlaub & Feiertage im Tagesumsatz ausgeglichen werden */
  const [pIncludeVacation, setPIncludeVacation] = useState(true);
  
  const [pOverTarget, setPOverTarget] = useState(false);
  const [pOverBreakeven, setPOverBreakeven] = useState(false);
  const [pOverHourly, setPOverHourly] = useState(false);
  
  const [pValTarget, setPValTarget] = useState(3.50);
  const [pValBreakeven, setPValBreakeven] = useState(2.80);
  const [pValHourly, setPValHourly] = useState(75.00);
  
  const [showProfitExpert, setShowProfitExpert] = useState(false);
  const [mpRate, setMpRate] = useState(STORE_HOURLY_RATE);
  const [mpMin, setMpMin] = useState(45);
  const [mpLocked, setMpLocked] = useState(true);

  const fT_p = pOverTarget ? pValTarget : STORE_FACTOR_TARGET;
  const fB_p = pOverBreakeven ? pValBreakeven : STORE_FACTOR_BREAKEVEN;
  const currentHourlyUsed = pOverHourly ? pValHourly : STORE_HOURLY_RATE;

  const mh_profit = Math.round((pWorkHours * WPM) * 100) / 100;
  const currentProfitWage = pMode === 'total' ? pWage : pHourly * mh_profit;
  const currentProfitHourly = mh_profit > 0 ? currentProfitWage / mh_profit : pHourly;
  
  const revT = currentProfitWage * fT_p;
  const revB = currentProfitWage * fB_p;
  const revA = mh_profit * currentHourlyUsed * prodFactor;
  const diff_profit = revA - revT;
  const reqHr_p = (mh_profit * prodFactor) > 0 ? revT / (mh_profit * prodFactor) : 0;
  // Wie im Excel: mit Urlaubsausgleich wird das Tagesziel auf die realen Anwesenheitstage umgelegt
  const vacationDivisor = pIncludeVacation ? Math.max(0.5, 1 - pufferFactor) : 1;
  const dailyTarget_p = (WPM * pDays) > 0 ? (revT / (WPM * pDays)) / vacationDivisor : 0;

  const [hrMode, setHrMode] = useState<'calc_hourly' | 'calc_brutto' | 'calc_hours'>('calc_hourly');
  const [hrBrutto, setHrBrutto] = useState(2227.00);
  const [hrHours, setHrHours] = useState(37.0);
  const [hrHourly, setHrHourly] = useState(13.90);

  const syncHR = (type: 'brutto' | 'hours' | 'hourly', val: number) => {
    if (type === 'brutto') { setHrBrutto(val); if (hrHours > 0) setHrHourly(val / (hrHours * WPM)); }
    else if (type === 'hours') { setHrHours(val); if (hrMode === 'calc_hourly' && val > 0) setHrHourly(hrBrutto / (val * WPM)); if (hrMode === 'calc_brutto') setHrBrutto(hrHourly * val * WPM); }
    else if (type === 'hourly') { setHrHourly(val); if (hrMode === 'calc_brutto') setHrBrutto(val * hrHours * WPM); if (hrMode === 'calc_hours' && val > 0) setHrHours(hrBrutto / (val * WPM)); }
  };

  const hrResultDisplay = hrMode === 'calc_hourly' ? fmt(hrHourly) + ` ${currency}/Std` : hrMode === 'calc_brutto' ? fmt(hrBrutto) + ` ${currency}` : hrHours.toFixed(1) + " Std";

  const [goalMode, setGoalMode] = useState<'target' | 'factor'>('factor');
  // 💉 CHIRURGIE-FIX: Lohn-Toggle State für Planungswerkzeug
  const [planWageMode, setPlanWageMode] = useState<'brutto' | 'hourly'>('brutto');
  const [goalTargetValue, setGoalTargetValue] = useState(8000.00); 
  const [goalFactorValue, setGoalFactorValue] = useState(fT_p);
  const [goalLocked, setGoalLocked] = useState(true);
  const [workDaysWeek] = useState(5);

  const planerDisplayValue = (() => {
    const baseTarget = goalMode === 'target' ? goalTargetValue : hrBrutto * goalFactorValue;
    if (goalMode === 'factor') return baseTarget;
    return (workDaysWeek * WPM) > 0 ? (baseTarget / (workDaysWeek * WPM)) * (1 + pufferFactor) : 0;
  })();

  const [utSales, setUtSales] = useState(8908.00);
  const [utWage, setUtWage] = useState(2227.00);
  const istFaktor = utWage > 0 ? utSales / utWage : 0;
  const tachoDiff = istFaktor - fT_p;

  // 💉 CHIRURGIE-FIX: Roh-Eingabestatus für ungestörtes Tippen
  const [percInput, setPercInput] = useState<string | number>(12.0);
  const [wmVal, setWmVal] = useState<string | number>(37.0);
  const [wmToMonth, setWmToMonth] = useState(true);
  const [amMode, setAmMode] = useState<'markup' | 'margin'>('markup');
  const [amEK, setAmEK] = useState<string | number>(68.00);
  const [amVar, setAmVar] = useState<string | number>(100.0); 
  const [rkMargin, setRkMargin] = useState<string | number>(20.0);
  const [rkDiscount, setRkDiscount] = useState<string | number>(10.0);
  const [zbDays, setZbDays] = useState<string | number>(5);
  const [zbCust, setZbCust] = useState<string | number>(1);
  const [zbUpsell, setZbUpsell] = useState<string | number>(30.00);
  const [zbModeYear, setZbModeYear] = useState(false);

  const percNum = Number(percInput) || 0;
  const wmValNum = Number(wmVal) || 0;
  const amEKNum = Number(amEK) || 0;
  const amVarNum = Number(amVar) || 0;
  const rkMarginNum = Number(rkMargin) || 0;
  const rkDiscountNum = Number(rkDiscount) || 0;
  const zbDaysNum = Number(zbDays) || 0;
  const zbCustNum = Number(zbCust) || 0;
  const zbUpsellNum = Number(zbUpsell) || 0;

  // SMART-LOGIC RABATT-KILLER
  const [rkLocked, setRkLocked] = useState(true);
  const autoMarginValue = useMemo(() => {
    const istP = (calcs.rentabilitaetIst || 0) * 100;
    const sollP = calcs.rentabilitaetSoll || 0;
    return istP > 0 ? Number(istP.toFixed(1)) : Number(sollP.toFixed(1));
  }, [calcs.rentabilitaetIst, calcs.rentabilitaetSoll]);

  useEffect(() => {
    if (rkLocked) setRkMargin(autoMarginValue);
  }, [autoMarginValue, rkLocked]);

  const percMonthRes = MONTHLY_TURNOVER_BRUTTO * (percNum / 100);

  const handleWmToggle = () => {
    const newVal = wmToMonth ? wmValNum * WPM : wmValNum / WPM;
    setWmVal(newVal);
    setWmToMonth(!wmToMonth);
  };
  const wmResVal = wmToMonth ? wmValNum * WPM : wmValNum / WPM;

  const handleAmToggle = () => {
    if (amMode === 'markup') {
      const VK = amEKNum * (1 + amVarNum / 100);
      setAmVar(VK);
    } else {
      const Markup = ((amVarNum - amEKNum) / amEKNum) * 100;
      setAmVar(Markup);
    }
    setAmMode(amMode === 'markup' ? 'margin' : 'markup');
  };

  const amResValue = amMode === 'markup' ? amEKNum * (1 + amVarNum / 100) : (amVarNum > 0 ? ((amVarNum - amEKNum) / amEKNum) * 100 : 0);
  const rkResValue = rkDiscountNum >= rkMarginNum ? "VERLUST!" : ((rkDiscountNum / (rkMarginNum - rkDiscountNum)) + 1).toFixed(1) + "x";
  const zbResValue = zbDaysNum * zbCustNum * zbUpsellNum * (zbModeYear ? 52 : WPM);

  useEffect(() => { if (tapeRef.current) tapeRef.current.scrollTop = tapeRef.current.scrollHeight; }, [calcHistory, formula]);

  useEffect(() => {
    setZbUpsell(settings.global?.Zusatzumsatz || 30.00);
    setMpRate(currentHourlyUsed);
  }, [STORE_HOURLY_RATE, settings.global?.Zusatzumsatz, currentHourlyUsed]);

  const clampWidth = (w: number) =>
    Math.min(CALC_WIDTH_MAX, Math.max(CALC_WIDTH_MIN, Math.min(w, (typeof window !== 'undefined' ? window.innerWidth : CALC_WIDTH_MAX) - 8)));

  const targetWidthForTab = (tab: 'calc' | 'profit' | 'hr' | 'biz') => {
    if (tab === 'calc') return CALC_WIDTH_DEFAULT;
    if (tab === 'profit') return CALC_WIDTH_PROFIT;
    return CALC_WIDTH_TOOLS;
  };

  const selectTab = (tab: 'calc' | 'profit' | 'hr' | 'biz') => {
    setActiveTab(tab);
    setPanelWidth(clampWidth(targetWidthForTab(tab)));
  };

  const resetToStandardSize = () => {
    setPanelWidth(clampWidth(targetWidthForTab(activeTab)));
    setZoomIndex(0);
  };

  const handleClose = () => {
    resetToStandardSize();
    onClose();
  };

  const handleResizeStart = (edge: 'left' | 'right') => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    isResizing.current = true;
    const startX = e.clientX;
    const startW = panelWidth;
    const onMove = (ev: MouseEvent) => {
      if (!isResizing.current) return;
      const dx = ev.clientX - startX;
      setPanelWidth(clampWidth(edge === 'right' ? startW + dx : startW - dx));
    };
    const onUp = () => {
      isResizing.current = false;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const zoom = zoomLevels[zoomIndex];
  // Zoom skaliert von oben — genug Höhe lassen, damit Profit-Tab nicht abgeschnitten wird
  const shellHeight = activeTab === 'calc' ? '720px' : '840px';
  const shellStyle: React.CSSProperties = {
    left: `calc(50% + ${position.x}px)`,
    top: `calc(10px + ${position.y}px)`,
    width: panelWidth,
    maxWidth: '100vw',
    height: `min(${shellHeight}, calc((100dvh - 20px) / ${zoom}))`,
    maxHeight: `calc((100dvh - 20px) / ${zoom})`,
    transform: `scale(${zoom}) translateX(-50%)`,
  };

  return (
    <div 
      className={`sys-calc sys-calc-shell fixed sm:rounded-[2.5rem] flex flex-col overflow-hidden z-[9999] transition-transform duration-200 origin-top text-left ${isWide ? 'sys-calc-wide' : ''}`}
      style={shellStyle}
    >
      <style>{calculatorStyles}</style>

      <div
        onMouseDown={handleResizeStart('left')}
        className="absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize z-[60] group"
        title="Breite ziehen"
      >
        <div className="absolute left-0.5 top-1/2 -translate-y-1/2 w-1 h-16 rounded-full opacity-40 group-hover:opacity-90 transition-opacity" style={{ background: 'var(--sc-accent)' }} />
      </div>
      <div
        onMouseDown={handleResizeStart('right')}
        className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize z-[60] group"
        title="Breite ziehen"
      >
        <div className="absolute right-0.5 top-1/2 -translate-y-1/2 w-1 h-16 rounded-full opacity-40 group-hover:opacity-90 transition-opacity" style={{ background: 'var(--sc-accent)' }} />
      </div>

      {/* HEADER — 3 Spalten, kein absolut zentrierter Titel über den Chips */}
      <div
        onMouseDown={onDragStart}
        className="sys-calc-header carbon-weave-bg px-2 flex-shrink-0 h-12 grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 select-none cursor-move"
      >
        <div className="flex items-center gap-1 pl-1 min-w-0">
            <button onClick={(e) => { e.stopPropagation(); setZoomIndex((zoomIndex + 1) % 3); }} className="sys-calc-chip p-1.5 rounded-full flex items-center justify-center gap-0.5 group shrink-0" title="Zoom">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/><path d="M11 8v6"/><path d="M8 11h6"/></svg>
              <span className="text-[8px] font-black uppercase">{Math.round(zoom * 100)}%</span>
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); resetToStandardSize(); }}
              className="sys-calc-chip p-1.5 rounded-full flex items-center justify-center shrink-0"
              title="Standardgröße"
              aria-label="Standardgröße"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M8 3H5a2 2 0 0 0-2 2v3"/><path d="M21 8V5a2 2 0 0 0-2-2h-3"/><path d="M3 16v3a2 2 0 0 0 2 2h3"/><path d="M16 21h3a2 2 0 0 0 2-2v-3"/></svg>
            </button>
        </div>
        <span className="sys-calc-accent text-[11px] sm:text-xs font-black uppercase tracking-[0.15em] text-center truncate pointer-events-none">
          Systemrechner
        </span>
        <button onClick={handleClose} className="sys-calc-muted hover:opacity-80 mr-1 justify-self-end" title="Schließen" aria-label="Schließen">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
        </button>
      </div>

      <div className="flex-1 overflow-hidden relative sys-calc-body">
        
        {/* TAB 1: RECHNER */}
        <div className={`tab-content flex flex-col h-full ${activeTab === 'calc' ? '' : 'hidden'}`}>
          <div className="flex justify-between items-center px-4 py-1.5 border-b shrink-0" style={{ borderColor: 'var(--sc-border)', background: 'color-mix(in srgb, var(--sc-surface) 70%, transparent)' }}>
            <div className="sys-calc-chip text-[10px] font-black uppercase px-2 py-1 rounded tracking-widest">MwSt: {vatRate}%</div>
            <div className="flex gap-2">
              <button onClick={() => setIsGross(!isGross)} className={`group px-2 py-1 sys-calc-chip rounded text-[10px] font-bold uppercase flex items-center gap-1.5 ${isGross ? 'opacity-50' : ''}`}>
                <span>{isGross ? 'Brutto' : 'Netto'}</span>
                <span className="text-[10px] opacity-70">↕</span>
              </button>
              <button onClick={() => setIsYear(!isYear)} className={`group px-2 py-1 sys-calc-chip rounded text-[10px] font-bold uppercase flex items-center gap-1.5 ${isYear ? 'text-blue-600 dark:text-blue-400' : 'opacity-50'}`}>
                <span>{isYear ? 'Jahr' : 'Monat'}</span>
                <span className="text-[10px] opacity-70">↕</span>
              </button>
            </div>
          </div>
          {/* Legende/Historie — nimmt den freien Platz (kein toter Graubereich über der Tastatur) */}
          <div
            ref={tapeRef}
            className="flex-1 min-h-[6rem] overflow-y-auto px-3 py-2 text-right font-mono custom-scrollbar sys-calc-body"
          >
            {calcHistory.length === 0 ? (
              <div className="sys-calc-muted text-[10px] uppercase tracking-widest text-center pt-4 opacity-50">
                Rechenweg erscheint hier nach =
              </div>
            ) : (
              <div className="space-y-1.5 text-xs">
                {calcHistory.map((entry, idx) => (
                  <div key={idx} className="flex justify-between gap-2 border-b pb-0.5" style={{ borderColor: 'var(--sc-border)' }}>
                    <span className="sys-calc-muted text-left break-all">{entry.f}</span>
                    <span className="sys-calc-accent font-bold shrink-0">={fmt(entry.r)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* Aktuelle Eingabe — eine Zeile */}
          <div className="shrink-0 px-3 py-1.5 border-t text-right font-mono" style={{ borderColor: 'var(--sc-border)', background: 'color-mix(in srgb, var(--sc-surface) 50%, transparent)' }}>
            <div className="flex items-baseline justify-between gap-2">
              <span className="sys-calc-accent text-[9px] italic shrink-0">Eingabe:</span>
              <span className="sys-calc-text font-bold text-sm tracking-wide truncate min-h-[1.25rem]">
                {formula ? prettyFormula(formula) : '—'}
              </span>
            </div>
          </div>
          {/* Netto / Monat / MwSt — ohne Großanzeige */}
          <div className="sys-calc-elevated px-3 py-1.5 shrink-0 border-t" style={{ borderColor: 'var(--sc-border)' }}>
            <div className="flex justify-between px-1">
              <div className="sys-calc-accent font-black text-[11px] uppercase">{isGross ? 'BRUTTO' : 'NETTO'}: {fmt(isGross ? evalForm(formula) * (1 + vatRate / 100) : evalForm(formula) / (1 + vatRate / 100))} {currency}</div>
              <div className="text-blue-600 dark:text-blue-400 font-black text-[11px] uppercase">{isYear ? 'JAHR' : 'MONAT'}: {fmt(isYear ? evalForm(formula) * 12 : evalForm(formula) / 12)} {currency}</div>
            </div>
            <div className="px-1 mt-0.5">
              <div className="text-amber-700 dark:text-amber-500/90 font-black text-[10px] uppercase tracking-wide">
                MwSt ({vatRate}%): {fmt(isGross
                  ? evalForm(formula) * (vatRate / 100)
                  : evalForm(formula) - (evalForm(formula) / (1 + vatRate / 100))
                )} {currency}
                <span className="ml-1 text-[9px] font-bold sys-calc-muted normal-case tracking-normal">
                  {isGross ? 'aufgeschlagen' : 'abgezogen'}
                </span>
              </div>
            </div>
          </div>
          {/* Großes Ergebnis — wo früher der leere Graustreifen war */}
          <div className="sys-calc-keypad shrink-0 px-3 py-2 border-t" style={{ borderColor: 'var(--sc-border)' }}>
            <div className="text-right sys-calc-text font-mono text-3xl font-light truncate tracking-tight leading-none">
              {fmt(evalForm(formula))}
            </div>
          </div>
          <div className="sys-calc-keypad shrink-0 pb-0">
            <div className="grid grid-cols-4 gap-1.5 p-2 w-full">
            {['AC','DEL','%','/','7','8','9','*','4','5','6','-','1','2','3','+','PM','0','.'].map(k => (
              <button key={k} onClick={() => handleCalcInput(k)} className={`sys-calc-key-btn rounded font-bold btn-shadow ${isWide ? 'py-3' : 'py-2'} ${k === 'AC' ? 'from-[#3a1d1d] to-[#1f0f0f] text-red-500 dark:text-red-400 bg-gradient-to-b' : k === 'DEL' ? 'calc-gradient sys-calc-muted' : (['%','/','*','-','+'].includes(k)) ? 'calc-gradient sys-calc-accent text-lg' : 'calc-gradient sys-calc-text text-lg'}`}>
                {k === '*' ? '×' : k === '/' ? '÷' : k === 'DEL' ? '⌫' : k === 'PM' ? '+/-' : k}
              </button>
            ))}
            <button onClick={() => handleCalcInput('=')} className={`sys-calc-key-btn calc-gradient rounded border-2 font-black btn-shadow equal-glow active:scale-95 transition-transform sys-calc-accent ${isWide ? 'py-3 text-xl' : 'py-2 text-lg'}`} style={{ borderColor: 'var(--sc-accent)' }}>=</button>
            </div>
          </div>
        </div>

        {/* TAB 2: PROFIT — Umsatz- & Lohnrechner (Excel-Erzählfluss, sauber & groß) */}
        <div className={`tab-content flex-col h-full sys-calc-body ${activeTab === 'profit' ? 'flex' : 'hidden'}`}>
          <div className="p-3.5 overflow-y-auto custom-scrollbar flex-1 pb-8 flex flex-col gap-3.5 text-left">
            <div className="sys-calc-panel rounded-xl p-4 shadow-xl shrink-0">
              {/* KOPFZEILE */}
              <div className="flex flex-wrap items-center justify-between gap-3 border-b sys-calc-divider pb-3 mb-3.5">
                <div className="min-w-0">
                  <div className="text-xs font-extrabold uppercase tracking-wider sys-calc-accent">
                    Umsatz- &amp; Lohnrechner
                  </div>
                  <h3 className="text-base font-black sys-calc-text leading-snug">
                    Bei Deinem Stundensatz (ohne Material) von:
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={() => setShowProfitExpert(true)}
                  title="Klicken, um einen anderen Stundensatz zu testen"
                  className="px-3.5 py-1.5 rounded-lg border-2 font-mono text-xl font-black sys-calc-accent shrink-0 transition-transform active:scale-95"
                  style={{ borderColor: 'var(--sc-accent)', background: 'var(--pe-bg-sunken)' }}
                >
                  {fmt(currentHourlyUsed)} {currency}
                </button>
              </div>

              {/* ZEILE 1 (EINZEILIG, OHNE ÜBERLAPPUNG): Lohn + Gegenwert + Wochenstunden */}
              <div className={`grid gap-3 mb-3.5 items-end ${isWide ? 'grid-cols-12' : 'grid-cols-2'}`}>
                {/* 1. Eingabe Lohn mit klarem Stundenlohn / Bruttolohn Schalter */}
                <div className={isWide ? 'col-span-5' : 'col-span-2'}>
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <span className="text-xs font-extrabold uppercase sys-calc-text">Geplanter Lohn</span>
                    <div
                      className="inline-flex rounded-md p-0.5 border"
                      style={{ borderColor: 'var(--sc-border-strong)', background: 'var(--sc-input)' }}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          if (pMode !== 'hourly') {
                            setPHourly(Number(currentProfitHourly.toFixed(2)));
                            setPMode('hourly');
                          }
                        }}
                        className={`px-2.5 py-0.5 text-[11px] font-extrabold uppercase rounded transition-all ${
                          pMode === 'hourly' ? 'sys-calc-accent shadow-sm' : 'sys-calc-muted'
                        }`}
                        style={pMode === 'hourly' ? { background: 'var(--sc-surface)' } : undefined}
                      >
                        Stundenlohn
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (pMode !== 'total') {
                            setPWage(Number(currentProfitWage.toFixed(2)));
                            setPMode('total');
                          }
                        }}
                        className={`px-2.5 py-0.5 text-[11px] font-extrabold uppercase rounded transition-all ${
                          pMode === 'total' ? 'sys-calc-accent shadow-sm' : 'sys-calc-muted'
                        }`}
                        style={pMode === 'total' ? { background: 'var(--sc-surface)' } : undefined}
                      >
                        Bruttolohn
                      </button>
                    </div>
                  </div>

                  <div className="relative">
                    <input 
                      type="number" 
                      step={pMode === 'total' ? "25" : "0.25"} 
                      value={activeField === 'pWage' ? (pMode === 'total' ? pWage : pHourly) : (pMode === 'total' ? pWage.toFixed(2) : pHourly.toFixed(2))} 
                      onFocus={(e) => handleFocus(e, 'pWage')} 
                      onKeyDown={handleKeyDown} 
                      onBlur={handleBlur} 
                      onChange={(e) => {
                        const val = Number(e.target.value);
                        if (pMode === 'total') setPWage(val);
                        else setPHourly(val);
                      }} 
                      className="noble-input has-stepper font-bold" 
                    />
                    <span className="unit-label with-stepper">{pMode === 'hourly' ? `${currency}/h` : currency}</span>
                    <div className="absolute right-1 top-1 bottom-1 w-9 flex flex-col gap-0.5 z-10">
                      <button type="button" onClick={() => pMode === 'total' ? setPWage((v: number) => v + 25) : setPHourly((v: number) => Number((v + 0.25).toFixed(2)))} className="sys-calc-stepper-btn">▲</button>
                      <button type="button" onClick={() => pMode === 'total' ? setPWage((v: number) => Math.max(0, v - 25)) : setPHourly((v: number) => Number(Math.max(0, v - 0.25).toFixed(2)))} className="sys-calc-stepper-btn">▼</button>
                    </div>
                  </div>
                </div>

                {/* 2. Umgerechneter Lohn (eigene saubere Box daneben — nichts überlappt!) */}
                <div className={isWide ? 'col-span-3' : 'col-span-1'}>
                  <div className="mb-1.5">
                    <span className="text-xs font-extrabold uppercase sys-calc-muted block truncate">
                      = {pMode === 'hourly' ? 'Monatsbrutto' : 'Stundenlohn'}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (pMode === 'hourly') {
                        setPWage(Number(currentProfitWage.toFixed(2)));
                        setPMode('total');
                      } else {
                        setPHourly(Number(currentProfitHourly.toFixed(2)));
                        setPMode('hourly');
                      }
                    }}
                    title="Klicken zum direkten Bearbeiten"
                    className="w-full h-[44px] rounded-lg border px-3 flex items-center justify-end font-mono text-base font-black sys-calc-accent hover:opacity-90 transition-opacity"
                    style={{ borderColor: 'var(--sc-border-strong)', background: 'var(--pe-bg-sunken)' }}
                  >
                    {pMode === 'hourly'
                      ? `${fmt(currentProfitWage)} ${currency}`
                      : `${fmt(currentProfitHourly)} ${currency}/h`}
                  </button>
                </div>

                {/* 3. Wochenstunden */}
                <div className={isWide ? 'col-span-4' : 'col-span-1'}>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-extrabold uppercase sys-calc-text">
                      Wochenstunden
                    </label>
                    <span className="text-xs font-mono font-bold sys-calc-muted">
                      = {mh_profit.toFixed(0)} Std./Mo.
                    </span>
                  </div>
                  <div className="relative">
                    <input type="number" step="0.5" value={activeField === 'pWorkHours' ? pWorkHours : pWorkHours.toFixed(1)} onFocus={(e) => handleFocus(e, 'pWorkHours')} onKeyDown={handleKeyDown} onBlur={handleBlur} onChange={(e) => setPWorkHours(Number(e.target.value))} className="noble-input has-stepper font-bold" />
                    <span className="unit-label with-stepper">Std.</span>
                    <div className="absolute right-1 top-1 bottom-1 w-9 flex flex-col gap-0.5 z-10">
                      <button type="button" onClick={() => setPWorkHours((v: number) => Number((v + 0.5).toFixed(1)))} className="sys-calc-stepper-btn">▲</button>
                      <button type="button" onClick={() => setPWorkHours((v: number) => Number(Math.max(0, v - 0.5).toFixed(1)))} className="sys-calc-stepper-btn">▼</button>
                    </div>
                  </div>
                </div>
              </div>

              {/* ZEILE 2: DIE 4 VERGLEICHS-SPALTEN (Faktor steht groß direkt in der jeweiligen Karte!) */}
              <div className={`grid gap-2.5 mb-3.5 text-center sys-calc-metrics ${isWide ? '' : 'grid-cols-2'}`}>
                {/* 1. Ziel-Umsatz */}
                <div className="sys-calc-metric">
                  <span className="sys-calc-metric-label">Ziel-Umsatz</span>
                  <span className="sys-calc-metric-value">{fmt0(revT)} {currency}</span>
                  <span className="text-xs font-extrabold text-emerald-600 dark:text-emerald-400">
                    Faktor {fT_p.toFixed(2)} (mit Gewinn)
                  </span>
                </div>

                {/* 2. Kostendeckung */}
                <div className="sys-calc-metric">
                  <span className="sys-calc-metric-label">Kostendeckung</span>
                  <span className="sys-calc-metric-value-plain">{fmt0(revB)} {currency}</span>
                  <span className="text-xs font-extrabold text-emerald-600 dark:text-emerald-400">
                    Faktor {fB_p.toFixed(2)} (Nullsumme)
                  </span>
                </div>

                {/* 3. Reine Handarbeit (Blau) */}
                <div className="sys-calc-metric sys-calc-tile-reach">
                  <span className="sys-calc-metric-label">Reine Handarbeit</span>
                  <span className="sys-calc-metric-value">{fmt0(revA)} {currency}</span>
                  <span className="sys-calc-metric-sub">
                    bei {Math.round(prodFactor * 100)} % Auslastung
                  </span>
                </div>

                {/* 4. Differenz */}
                <div className={`sys-calc-metric ${diff_profit >= 0 ? 'sys-calc-tile-ok' : 'sys-calc-tile-bad'}`}>
                  <span className="sys-calc-metric-label">Differenz</span>
                  <span className="sys-calc-metric-value">
                    {diff_profit >= 0 ? '+' : ''}{fmt0(diff_profit)} {currency}
                  </span>
                  <span className="sys-calc-metric-sub">
                    {diff_profit >= 0 ? 'Ziel erreicht ✓' : 'fehlt jeden Monat'}
                  </span>
                </div>
              </div>

              {/* ZEILE 3: BENÖTIGTER UMSATZ PRO STD. & PRO TAG + URLAUBSAUSGLEICH */}
              <div className="sys-calc-need-bar p-3 mb-3 flex flex-col gap-2.5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-center">
                  <div className="flex items-center justify-between sm:border-r sys-calc-divider sm:pr-3">
                    <div>
                      <span className="text-xs sys-calc-text font-extrabold block">
                        Benötigter Umsatz pro Std.
                      </span>
                      <span className="text-xs sys-calc-muted">
                        für {fmt0(revT)} {currency} Ziel-Umsatz
                      </span>
                    </div>
                    <span className="text-xl font-mono font-black text-emerald-600 dark:text-emerald-400 shrink-0 ml-2">
                      {fmt0(reqHr_p)} {currency}
                    </span>
                  </div>

                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <span className="text-xs sys-calc-text font-extrabold block">
                        Benötigter Umsatz pro Tag
                      </span>
                      <span className="text-xs sys-calc-muted">
                        bei {pDays} Arbeitstagen / Woche
                      </span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-xl font-mono font-black text-emerald-600 dark:text-emerald-400">
                        {fmt0(dailyTarget_p)} {currency}
                      </span>
                      <div className="flex items-center rounded border h-8 overflow-hidden" style={{ borderColor: 'var(--sc-border-strong)', background: 'var(--pe-bg-sunken)' }} title="Arbeitstage pro Woche">
                        <button type="button" onClick={() => setPDays(v => Math.max(1, v - 1))} className="w-7 h-full flex items-center justify-center text-sm font-bold sys-calc-text hover:opacity-80">−</button>
                        <div className="px-2 flex items-center justify-center h-full border-x" style={{ borderColor: 'var(--sc-border)' }}>
                          <span className="text-xs font-extrabold sys-calc-text">{pDays}T</span>
                        </div>
                        <button type="button" onClick={() => setPDays(v => Math.min(7, v + 1))} className="w-7 h-full flex items-center justify-center text-sm font-bold sys-calc-text hover:opacity-80">+</button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Haken „inkl. Urlaubsausgleich“ */}
                <div className="flex flex-wrap items-center justify-between gap-2 border-t sys-calc-divider pt-2.5">
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={pIncludeVacation}
                      onChange={(e) => setPIncludeVacation(e.target.checked)}
                      className="noble-checkbox"
                    />
                    <span className="text-xs font-extrabold sys-calc-text">
                      Inkl. Urlaubs- &amp; Feiertagsausgleich ({Math.round(pufferFactor * 100)} %)
                    </span>
                  </label>
                  <span className="text-xs sys-calc-muted">
                    Urlaubstage werden auf Arbeitstage umgelegt
                  </span>
                </div>
              </div>

              {/* ERKLÄR-SATZ AUS DEM EXCEL (gut lesbar in 12px) */}
              <div className="rounded-lg px-3.5 py-2.5 mb-3 text-xs leading-relaxed sys-calc-text border sys-calc-divider" style={{ background: 'var(--pe-bg-sunken)' }}>
                Bei der angegebenen Arbeitszeit ({Math.round(prodFactor * 100)} % Auslastung) und Lohnfortzahlung kann ein Mitarbeiter, der <strong>keine Kunden parallel bedient</strong> (Assistenz nutzt) und <strong>ohne Verkauf &amp; Spezial-Dienstleistungen</strong> arbeitet, durch reine Handarbeit maximal den <strong className="text-blue-600 dark:text-blue-400">blau angezeigten Umsatz ({fmt0(revA)} {currency})</strong> erzielen.
              </div>
              
              {/* MANUELL EINTRAGEN */}
              <div className="border-t sys-calc-divider pt-2.5 text-left">
                <button
                  type="button"
                  onClick={() => setShowProfitExpert(!showProfitExpert)}
                  className="w-full py-2 px-3 rounded-lg sys-calc-factor-bar flex items-center justify-between text-xs sys-calc-text font-extrabold uppercase tracking-wide hover:opacity-90 transition-opacity"
                >
                  <span>⚙️ Manuell eintragen (Faktoren / Stundensatz testen)</span>
                  <span className="sys-calc-accent">{showProfitExpert ? '▲ zuklappen' : '▼ aufklappen'}</span>
                </button>
                {showProfitExpert && (
                  <div className="mt-2.5 grid grid-cols-1 sm:grid-cols-3 gap-3 sys-calc-factor-bar p-3 text-left">
                    <label className="flex items-center gap-2.5 cursor-pointer">
                      <input type="checkbox" checked={pOverTarget} onChange={(e) => setPOverTarget(e.target.checked)} className="noble-checkbox" />
                      <div className="flex-1">
                        <span className="text-xs font-bold sys-calc-muted block">Ziel-Faktor (Salon: {STORE_FACTOR_TARGET.toFixed(2)})</span>
                        <input type="number" step="0.05" value={activeField === 'pValTarget' ? pValTarget : pValTarget.toFixed(2)} onFocus={(e) => { setPOverTarget(true); handleFocus(e, 'pValTarget'); }} onKeyDown={handleKeyDown} onBlur={handleBlur} onChange={(e) => { setPOverTarget(true); setPValTarget(Number(e.target.value)); }} className="w-full bg-transparent sys-calc-text text-base font-mono font-bold border-b outline-none py-0.5" style={{ borderColor: 'var(--sc-border-strong)' }} />
                      </div>
                    </label>
                    <label className="flex items-center gap-2.5 cursor-pointer">
                      <input type="checkbox" checked={pOverBreakeven} onChange={(e) => setPOverBreakeven(e.target.checked)} className="noble-checkbox" />
                      <div className="flex-1">
                        <span className="text-xs font-bold sys-calc-muted block">Kostendeckung (Salon: {STORE_FACTOR_BREAKEVEN.toFixed(2)})</span>
                        <input type="number" step="0.05" value={activeField === 'pValBreakeven' ? pValBreakeven : pValBreakeven.toFixed(2)} onFocus={(e) => { setPOverBreakeven(true); handleFocus(e, 'pValBreakeven'); }} onKeyDown={handleKeyDown} onBlur={handleBlur} onChange={(e) => { setPOverBreakeven(true); setPValBreakeven(Number(e.target.value)); }} className="w-full bg-transparent sys-calc-text text-base font-mono font-bold border-b outline-none py-0.5" style={{ borderColor: 'var(--sc-border-strong)' }} />
                      </div>
                    </label>
                    <label className="flex items-center gap-2.5 cursor-pointer">
                      <input type="checkbox" checked={pOverHourly} onChange={(e) => setPOverHourly(e.target.checked)} className="noble-checkbox" />
                      <div className="flex-1">
                        <span className="text-xs font-bold sys-calc-muted block">Stundensatz ({currency})</span>
                        <input type="number" step="1" value={activeField === 'pValHourly' ? pValHourly : pValHourly.toFixed(2)} onFocus={(e) => { setPOverHourly(true); handleFocus(e, 'pValHourly'); }} onKeyDown={handleKeyDown} onBlur={handleBlur} onChange={(e) => { setPOverHourly(true); setPValHourly(Number(e.target.value)); }} className="w-full bg-transparent sys-calc-text text-base font-mono font-bold border-b outline-none py-0.5" style={{ borderColor: 'var(--sc-border-strong)' }} />
                      </div>
                    </label>
                  </div>
                )}
              </div>
            </div>

            {/* MINUTES CHECK */}
            <div className="sys-calc-panel rounded-lg p-3 shadow-xl shrink-0 text-left">
              <h3 className="text-[#E6C299] text-xs font-bold uppercase tracking-widest mb-2 border-b border-white/10 pb-1">⏱️ Minuten-Preis Check</h3>
              <div className={`grid gap-2 ${isWide ? 'grid-cols-3 items-end' : 'grid-cols-2'}`}>
                <div className="relative">
                  <div className="flex justify-between items-center mb-1">
                    <label className="text-[11px] text-slate-400 uppercase font-bold">Stundensatz</label>
                    <button onClick={() => setMpLocked(!mpLocked)} className="text-slate-500 transition-colors hover:text-white">{mpLocked ? '🔒' : '🔓'}</button>
                  </div>
                  <div className="relative">
                    <input type="number" step="0.01" value={activeField === 'mpRate' ? mpRate : mpRate.toFixed(2)} onFocus={(e) => handleFocus(e, 'mpRate')} onKeyDown={handleKeyDown} onBlur={handleBlur} onChange={(e) => setMpRate(Number(e.target.value))} disabled={mpLocked} className="noble-input has-stepper font-bold" />
                    <span className="unit-label with-stepper">{currency}</span>
                    <div className={`absolute right-1 top-1 bottom-1 w-9 flex flex-col gap-0.5 z-10 transition-opacity ${mpLocked ? 'opacity-30' : 'opacity-100'}`}>
                      <button onClick={() => !mpLocked && setMpRate((v: number) => Number((v + 0.5).toFixed(2)))} className={`flex-1 bg-white/10 rounded-sm text-[10px] text-[#E6C299] ${mpLocked ? 'cursor-not-allowed' : 'hover:bg-white/20'}`}>▲</button>
                      <button onClick={() => !mpLocked && setMpRate((v: number) => Number(Math.max(0, v - 0.5).toFixed(2)))} className={`flex-1 bg-white/10 rounded-sm text-[10px] text-[#E6C299] ${mpLocked ? 'cursor-not-allowed' : 'hover:bg-white/20'}`}>▼</button>
                    </div>
                  </div>
                </div>
                <div className="relative"><label className="text-[11px] text-slate-400 uppercase font-bold block mb-1">Dauer</label><div className="relative"><input type="number" value={activeField === 'mpMin' ? mpMin : mpMin} onFocus={(e) => handleFocus(e, 'mpMin')} onKeyDown={handleKeyDown} onBlur={handleBlur} onChange={(e) => setMpMin(Number(e.target.value))} className="noble-input has-stepper font-bold" /><span className="unit-label with-stepper">Min</span><div className="absolute right-1 top-1 bottom-1 w-9 flex flex-col gap-0.5 z-10"><button onClick={() => setMpMin(v => v + 5)} className="flex-1 bg-white/10 rounded-sm text-[10px] text-[#E6C299]">▲</button><button onClick={() => setMpMin(v => Math.max(0, v - 5))} className="flex-1 bg-white/10 rounded-sm text-[10px] text-[#E6C299]">▼</button></div></div></div>
                {isWide && (
                  <div className="bg-black/50 rounded p-3 flex flex-col justify-center border border-[#E6C299]/20 h-[46px]"><span className="text-slate-400 text-[9px] uppercase font-bold">Mindestpreis</span><span className="text-lg font-mono text-[#E6C299] font-bold leading-tight">{fmt((mpRate / 60) * mpMin)} {currency}</span></div>
                )}
              </div>
              {!isWide && (
                <div className="bg-black/50 rounded p-3 flex justify-between items-center border border-[#E6C299]/20 mt-1"><span className="text-slate-400 text-[11px] uppercase font-bold">Mindestpreis:</span><span className="text-xl font-mono text-[#E6C299] font-bold">{fmt((mpRate / 60) * mpMin)} {currency}</span></div>
              )}
            </div>
          </div>
        </div>

        {/* TAB 3: PERSONAL / HR */}
        <div className={`tab-content flex-col h-full sys-calc-body ${activeTab === 'hr' ? 'flex' : 'hidden'}`}>
          <div className="p-3 overflow-y-auto custom-scrollbar flex-1 pb-8 flex flex-col gap-3 text-left">
            <div className="sys-calc-panel rounded-lg p-3 shadow-xl shrink-0">
              <h3 className="text-blue-400 text-xs font-bold uppercase tracking-widest mb-2 border-b border-white/10 pb-1">💼 Lohn & Zeit <span className="opacity-50 text-[9px] normal-case tracking-normal ml-1">(Ø {WPM} Wo.)</span></h3>
              <div className={`mb-2 ${isWide ? 'grid grid-cols-3 gap-2 items-end' : ''}`}>
                <div className={isWide ? '' : 'mb-2'}><label className="text-[11px] text-slate-400 uppercase font-bold block mb-1">Berechnung wählen</label><select value={hrMode} onChange={(e: any) => setHrMode(e.target.value)} className="w-full bg-black border border-slate-700 rounded p-2 text-[11px] text-white uppercase font-bold h-10 shadow-sm"><option value="calc_hourly">Stundenlohn</option><option value="calc_brutto">Bruttolohn</option><option value="calc_hours">Wochenstunden</option></select></div>
                {isWide ? (
                  <>
                    <div><label className="text-[11px] text-slate-400 uppercase font-bold block mb-1">{hrMode === 'calc_brutto' ? 'STD.-LOHN' : 'BRUTTO'}</label><div className="relative"><input type="number" step="0.01" value={activeField === 'hrB' ? (hrMode === 'calc_brutto' ? hrHourly : hrBrutto) : (hrMode === 'calc_brutto' ? hrHourly.toFixed(2) : hrBrutto.toFixed(2))} onFocus={(e) => handleFocus(e, 'hrB')} onKeyDown={handleKeyDown} onBlur={handleBlur} onChange={(e) => syncHR(hrMode === 'calc_brutto' ? 'hourly' : 'brutto', Number(e.target.value))} className={`noble-input font-bold text-white ${hrMode === 'calc_brutto' ? 'has-stepper' : 'pr-12'}`} /><span className={`unit-label ${hrMode === 'calc_brutto' ? 'with-stepper' : ''}`}>{currency}</span></div></div>
                    <div><label className="text-[11px] text-slate-400 uppercase font-bold block mb-1">{hrMode === 'calc_hours' ? 'STD.-LOHN' : 'STD./WO.'}</label><div className="relative"><input type="number" step="0.1" value={activeField === 'hrS' ? (hrMode === 'calc_hours' ? hrHourly : hrHours) : (hrMode === 'calc_hours' ? hrHourly.toFixed(2) : hrHours.toFixed(1))} onFocus={(e) => handleFocus(e, 'hrS')} onKeyDown={handleKeyDown} onBlur={handleBlur} onChange={(e) => syncHR(hrMode === 'calc_hours' ? 'hourly' : 'hours', Number(e.target.value))} className="noble-input has-stepper font-bold text-white" /><span className="unit-label with-stepper">{hrMode === 'calc_hours' ? currency : 'STD'}</span><div className="absolute right-1 top-1 bottom-1 w-9 flex flex-col gap-0.5 z-10"><button onClick={() => { if(hrMode === 'calc_hours') syncHR('hourly', Number((hrHourly + 0.1).toFixed(2))); else syncHR('hours', Number((hrHours + 0.5).toFixed(1))); }} className="flex-1 bg-white/10 rounded-sm text-[10px] flex items-center justify-center text-blue-400">▲</button><button onClick={() => { if(hrMode === 'calc_hours') syncHR('hourly', Number(Math.max(0, hrHourly - 0.1).toFixed(2))); else syncHR('hours', Number(Math.max(0, hrHours - 0.5).toFixed(1))); }} className="flex-1 bg-white/10 rounded-sm text-[10px] flex items-center justify-center text-blue-400">▼</button></div></div></div>
                  </>
                ) : null}
              </div>
              {!isWide && (
              <div className="grid grid-cols-2 gap-2">
                <div><label className="text-[11px] text-slate-400 uppercase font-bold block mb-1">{hrMode === 'calc_brutto' ? 'STD.-LOHN' : 'BRUTTO'}</label><div className="relative"><input type="number" step="0.01" value={activeField === 'hrB' ? (hrMode === 'calc_brutto' ? hrHourly : hrBrutto) : (hrMode === 'calc_brutto' ? hrHourly.toFixed(2) : hrBrutto.toFixed(2))} onFocus={(e) => handleFocus(e, 'hrB')} onKeyDown={handleKeyDown} onBlur={handleBlur} onChange={(e) => syncHR(hrMode === 'calc_brutto' ? 'hourly' : 'brutto', Number(e.target.value))} className={`noble-input font-bold text-white ${hrMode === 'calc_brutto' ? 'has-stepper' : 'pr-12'}`} /><span className={`unit-label ${hrMode === 'calc_brutto' ? 'with-stepper' : ''}`}>{currency}</span></div></div>
                <div><label className="text-[11px] text-slate-400 uppercase font-bold block mb-1">{hrMode === 'calc_hours' ? 'STD.-LOHN' : 'STD./WO.'}</label><div className="relative"><input type="number" step="0.1" value={activeField === 'hrS' ? (hrMode === 'calc_hours' ? hrHourly : hrHours) : (hrMode === 'calc_hours' ? hrHourly.toFixed(2) : hrHours.toFixed(1))} onFocus={(e) => handleFocus(e, 'hrS')} onKeyDown={handleKeyDown} onBlur={handleBlur} onChange={(e) => syncHR(hrMode === 'calc_hours' ? 'hourly' : 'hours', Number(e.target.value))} className="noble-input has-stepper font-bold text-white" /><span className="unit-label with-stepper">{hrMode === 'calc_hours' ? currency : 'STD'}</span><div className="absolute right-1 top-1 bottom-1 w-9 flex flex-col gap-0.5 z-10"><button onClick={() => { if(hrMode === 'calc_hours') syncHR('hourly', Number((hrHourly + 0.1).toFixed(2))); else syncHR('hours', Number((hrHours + 0.5).toFixed(1))); }} className="flex-1 bg-white/10 rounded-sm text-[10px] flex items-center justify-center text-blue-400">▲</button><button onClick={() => { if(hrMode === 'calc_hours') syncHR('hourly', Number(Math.max(0, hrHourly - 0.1).toFixed(2))); else syncHR('hours', Number(Math.max(0, hrHours - 0.5).toFixed(1))); }} className="flex-1 bg-white/10 rounded-sm text-[10px] flex items-center justify-center text-blue-400">▼</button></div></div></div>
              </div>
              )}
              <div className="bg-black/50 rounded p-3 text-center border border-blue-400/20 mt-1"><div className="text-slate-400 text-[11px] uppercase font-bold mb-0.5">ERGEBNIS</div><div className="text-xl font-mono text-white font-bold">{hrResultDisplay}</div></div>
            </div>

            <div className="sys-calc-panel rounded-lg p-3 shadow-xl shrink-0">
              <div className="flex flex-col gap-2 border-b border-white/10 pb-2 mb-2">
                <div className="flex items-center gap-2">
                    <span className="text-sm">🎯</span>
                    <h3 className="text-[#E6C299] text-xs font-bold uppercase tracking-widest">Planungs-Werkzeug</h3>
                </div>
                <div onClick={() => setGoalMode(goalMode === 'factor' ? 'target' : 'factor')} className="switcher-track">
                  <div className={`switcher-item ${goalMode === 'factor' ? 'active' : 'inactive'}`}>Lohnfaktor</div>
                  <div className="px-2 text-[#E6C299] text-[12px] animate-pulse">⇆</div>
                  <div className={`switcher-item ${goalMode === 'target' ? 'active' : 'inactive'}`}>Stunden</div>
                </div>
              </div>
              <div className={`grid gap-2 mb-1 ${isWide ? 'grid-cols-3 items-end' : 'grid-cols-2'}`}>
                <div>
                  <div className="flex justify-between items-center mb-1 text-left">
                    <label className="text-[11px] text-slate-400 uppercase font-bold text-left">{goalMode === 'target' ? 'MONATS-ZIEL' : (planWageMode === 'brutto' ? 'BRUTTO' : 'STD.-LOHN')}</label>
                    {goalMode === 'factor' && (
                      <div className="flex bg-black rounded p-0.5 border border-white/10">
                        <button onClick={() => setPlanWageMode('brutto')} className={`px-1.5 py-0.5 text-[8px] font-bold uppercase rounded transition-all ${planWageMode === 'brutto' ? 'text-[#E6C299] bg-[#1a1c24]' : 'text-slate-500'}`}>Gesamt</button>
                        <button onClick={() => setPlanWageMode('hourly')} className={`px-1.5 py-0.5 text-[8px] font-bold uppercase rounded transition-all ${planWageMode === 'hourly' ? 'text-[#E6C299] bg-[#1a1c24]' : 'text-slate-500'}`}>/Std.</button>
                      </div>
                    )}
                  </div>
                  <div className="relative">
                    <input 
                      type="number" 
                      step="0.01" 
                      value={activeField === 'goalV' ? (goalMode === 'target' ? goalTargetValue : (planWageMode === 'brutto' ? hrBrutto : hrHourly)) : (goalMode === 'target' ? goalTargetValue.toFixed(2) : (planWageMode === 'brutto' ? hrBrutto.toFixed(2) : hrHourly.toFixed(2)))} 
                      onFocus={(e) => handleFocus(e, 'goalV')} 
                      onKeyDown={handleKeyDown} 
                      onBlur={handleBlur} 
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        if(goalMode === 'target') setGoalTargetValue(v); 
                        else syncHR(planWageMode === 'brutto' ? 'brutto' : 'hourly', v);
                      }} 
                      className="noble-input font-bold text-white text-left" 
                    />
                    <span className="unit-label">{goalMode === 'target' ? currency : (planWageMode === 'hourly' ? currency + '/h' : currency)}</span>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between items-center mb-1 text-left">
                    <label className="text-[11px] text-slate-400 uppercase font-bold text-left">{goalMode === 'target' ? 'STD./WOCHE' : 'LOHNFAKTOR'}</label>
                    {goalMode === 'factor' && <button onClick={() => setGoalLocked(!goalLocked)} className="text-slate-500 hover:text-white transition-colors">{goalLocked ? '🔒' : '🔓'}</button>}
                  </div>
                  <div className="relative">
                    <input type="number" step={goalMode === 'target' ? "0.1" : "0.01"} value={activeField === 'goalF' ? (goalMode === 'target' ? hrHours : goalFactorValue) : (goalMode === 'target' ? hrHours.toFixed(1) : goalFactorValue.toFixed(2))} onFocus={(e) => handleFocus(e, 'goalF')} onKeyDown={handleKeyDown} onBlur={handleBlur} onChange={(e) => { const v = Number(e.target.value); if(goalMode === 'target') syncHR('hours', v); else if(!goalLocked) setGoalFactorValue(v); }} disabled={goalMode === 'factor' && goalLocked} className={`noble-input has-stepper font-bold ${goalMode === 'factor' && goalLocked ? 'text-[#E6C299]' : 'text-white'}`} />
                    <span className="unit-label with-stepper">{goalMode === 'target' ? 'STD' : 'x'}</span>
                    <div className={`absolute right-1 top-1 bottom-1 w-9 flex flex-col gap-0.5 z-10 transition-opacity ${goalMode === 'factor' && goalLocked ? 'opacity-30' : 'opacity-100'}`}>
                      <button onClick={() => { if(goalMode === 'target') syncHR('hours', hrHours + 0.5); else if(!goalLocked) setGoalFactorValue((v: number) => Number((v + 0.1).toFixed(2))); }} className={`flex-1 bg-white/10 rounded-sm text-[10px] text-[#E6C299] ${(goalMode === 'factor' && goalLocked) ? 'cursor-not-allowed' : 'hover:bg-white/20'}`}>▲</button>
                      <button onClick={() => { if(goalMode === 'target') syncHR('hours', Math.max(0, hrHours - 0.5)); else if(!goalLocked) setGoalFactorValue((v: number) => Number(Math.max(0, v - 0.1).toFixed(2))); }} className={`flex-1 bg-white/10 rounded-sm text-[10px] text-[#E6C299] ${(goalMode === 'factor' && goalLocked) ? 'cursor-not-allowed' : 'hover:bg-white/20'}`}>▼</button>
                    </div>
                  </div>
                </div>
                {isWide && (
                  <div className="bg-black/50 rounded p-3 border border-[#E6C299]/20 text-left self-stretch flex flex-col justify-center">
                    <span className="text-slate-400 text-[10px] uppercase font-bold">{goalMode === 'factor' ? 'Monats-Ziel' : 'Ziel / Std.'}</span>
                    <span className="text-lg font-mono text-[#E6C299] font-bold leading-tight">{fmt(planerDisplayValue)} {currency}</span>
                    <span className="text-[8px] text-slate-500">(inkl. {Math.round(pufferFactor*100)}% Puffer)</span>
                  </div>
                )}
              </div>
              {!isWide && (
                <div className="bg-black/50 rounded p-3 border border-[#E6C299]/20 mt-1 text-left"><div className="flex justify-between items-center mb-1"><span className="text-slate-400 text-[11px] uppercase font-bold">{goalMode === 'factor' ? 'MONATS-ZIEL:' : 'Ziel pro Stunde:'}</span><span className="text-xl font-mono text-[#E6C299] font-bold text-right">{fmt(planerDisplayValue)} {currency}</span></div><div className="text-[9px] text-slate-500 text-right leading-tight">(inkl. {Math.round(pufferFactor*100)}% Puffer)</div></div>
              )}
            </div>

            <div className="sys-calc-panel rounded-lg p-3 shadow-xl shrink-0">
              <h3 className="text-green-600 dark:text-green-400 text-xs font-bold uppercase tracking-widest mb-2 border-b border-white/10 pb-1">⏱️ Umsatz-Tacho</h3>
              <div className={`grid gap-2 mb-1 ${isWide ? 'grid-cols-3 items-end' : 'grid-cols-2'}`}>
                <div className="relative"><label className="text-[11px] text-slate-400 uppercase font-bold block mb-1">IST-Umsatz</label><input type="number" step="0.01" value={activeField === 'utS' ? utSales : utSales.toFixed(2)} onFocus={(e) => handleFocus(e, 'utS')} onKeyDown={handleKeyDown} onBlur={handleBlur} onChange={(e) => setUtSales(Number(e.target.value))} className="noble-input font-bold" /><span className="unit-label">{currency}</span></div>
                <div className="relative"><label className="text-[11px] text-slate-400 uppercase font-bold block mb-1">Bruttolohn</label><input type="number" step="0.01" value={activeField === 'utW' ? utWage : utWage.toFixed(2)} onFocus={(e) => handleFocus(e, 'utW')} onKeyDown={handleKeyDown} onBlur={handleBlur} onChange={(e) => setUtWage(Number(e.target.value))} className="noble-input font-bold" /><span className="unit-label">{currency}</span></div>
                {isWide && (
                  <div className="bg-black/50 rounded p-3 text-center border border-[#E6C299]/20 self-stretch flex flex-col justify-center">
                    <div className="text-slate-400 text-[10px] uppercase font-bold">IST-Faktor</div>
                    <div className="text-xl font-black font-mono text-white leading-tight">{istFaktor.toFixed(2)} x</div>
                    <div className={`text-[9px] font-bold uppercase ${tachoDiff < -0.5 ? 'text-red-400' : 'text-green-500'}`}>{tachoDiff < -1 ? 'KRITISCH' : tachoDiff < 0 ? 'RISIKO' : 'ZIEL ERREICHT'}</div>
                  </div>
                )}
              </div>
              {!isWide && (
                <div className="bg-black/50 rounded p-3 text-center border border-[#E6C299]/20 mt-1"><div className="text-slate-400 text-[11px] uppercase font-bold mb-0.5">IST-Faktor</div><div className="text-2xl font-black font-mono text-white">{istFaktor.toFixed(2)} x</div><div className={`text-[10px] font-bold uppercase mt-1 ${tachoDiff < -0.5 ? 'text-red-400' : 'text-green-500'}`}>{tachoDiff < -1 ? 'KRITISCH' : tachoDiff < 0 ? 'RISIKO' : 'ZIEL ERREICHT'}</div></div>
              )}
            </div>
          </div>
        </div>

        {/* TAB 4: BUSINESS */}
        <div className={`tab-content flex-col h-full sys-calc-body ${activeTab === 'biz' ? 'flex' : 'hidden'}`}>
          <div className="p-3 overflow-y-auto custom-scrollbar flex-1 pb-8 flex flex-col gap-3 text-left">
            <div className="sys-calc-panel rounded-lg p-3 shadow-xl shrink-0">
              <h3 className="text-blue-600 dark:text-blue-400 text-xs font-bold uppercase tracking-widest mb-2 border-b border-white/10 pb-1">🧮 Prozent-Check</h3>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <label className="text-[10px] text-slate-400 uppercase font-bold block mb-1">Anteil %</label>
                  <div className="relative">
                    <input 
                      type="number" 
                      step="0.1" 
                      value={activeField === 'percI' ? percInput : percNum.toFixed(1)} 
                      onFocus={(e) => handleFocus(e, 'percI')} 
                      onKeyDown={handleKeyDown} 
                      onBlur={handleBlur} 
                      onChange={(e) => setPercInput(e.target.value as any)} 
                      className="noble-input has-stepper font-bold text-white text-center" 
                    />
                    <span className="unit-label with-stepper">%</span>
                    <div className="absolute right-1 top-1 bottom-1 w-8 flex flex-col gap-0.5 z-10">
                      <button onClick={() => setPercInput(Number((percNum + 0.1).toFixed(1)))} className="flex-1 bg-white/10 rounded-sm text-[10px] text-[#E6C299]">▲</button>
                      <button onClick={() => setPercInput(Number(Math.max(0, percNum - 0.1).toFixed(1)))} className="flex-1 bg-white/10 rounded-sm text-[10px] text-[#E6C299]">▼</button>
                    </div>
                  </div>
                </div>
                <div><label className="text-[10px] text-slate-400 uppercase font-bold block mb-1">Monat</label><div className="bg-black border border-slate-700 rounded h-10 flex items-center justify-center font-mono font-bold text-[#E6C299] text-sm">{fmt0(percMonthRes)} {currency}</div></div>
                <div><label className="text-[10px] text-slate-400 uppercase font-bold block mb-1">Jahr ({bwaPeriod} Mon.)</label><div className="bg-black border border-slate-700 rounded h-10 flex items-center justify-center font-mono font-bold text-[#E6C299] text-sm">{fmt0(percMonthRes * bwaPeriod)} {currency}</div></div>
              </div>
            </div>
            
            <div className="sys-calc-panel rounded-lg p-3 shadow-xl shrink-0">
              <h3 className="text-orange-600 dark:text-orange-400 text-xs font-bold uppercase tracking-widest mb-2 border-b border-white/10 pb-1">📅 Wochen ↔ Monat ({WPM})</h3>
              <div className="flex items-center gap-2 justify-between">
                <div className="relative flex-1">
                  <label className="text-[11px] text-slate-400 font-bold block mb-1">{wmToMonth ? 'Std./Wo.' : 'Monat (Std)'}</label>
                  <input type="number" step="0.1" value={activeField === 'wmV' ? wmVal : wmValNum.toFixed(1)} onFocus={(e) => handleFocus(e, 'wmV')} onKeyDown={handleKeyDown} onBlur={handleBlur} onChange={(e) => setWmVal(e.target.value as any)} className="noble-input font-bold text-left" />
                  <span className="unit-label">h</span>
                </div>
                <div className="pt-4 text-center"><button onClick={handleWmToggle} className="bg-black/40 border border-[#E6C299] rounded px-3 py-1.5 text-[#E6C299] font-bold transition-all hover:scale-110 active:rotate-180 duration-300">⇆</button></div>
                <div className="relative flex-1"><label className="text-[11px] text-slate-400 font-bold block mb-1">{wmToMonth ? 'Monat (Std)' : 'Std./Wo.'}</label><input type="text" value={wmResVal.toFixed(1)} className="noble-input font-bold bg-[#1a1c24] text-slate-400 border-slate-700 text-left" disabled /><span className="unit-label">h</span></div>
              </div>
            </div>
            
            <div className="sys-calc-panel rounded-lg p-3 shadow-xl shrink-0">
              <div className="flex justify-between items-center border-b border-white/10 pb-2 mb-2">
                <h3 className="text-blue-600 dark:text-blue-400 text-xs font-bold uppercase tracking-widest flex items-center gap-1">🧮 Aufschlag & Marge</h3>
                <div onClick={handleAmToggle} className="bg-black rounded p-0.5 border border-white/10 flex cursor-pointer transition-all hover:border-[#E6C299]/30">
                  <div className={`px-2 py-1 text-[9px] font-bold uppercase rounded ${amMode === 'markup' ? 'text-[#E6C299] bg-[#1a1c24]' : 'text-slate-500'}`}>Aufschlag</div>
                  <div className={`px-2 py-1 text-[9px] font-bold uppercase rounded ${amMode === 'margin' ? 'text-[#E6C299] bg-[#1a1c24]' : 'text-slate-500'}`}>Marge</div>
                </div>
              </div>
              <div className="grid grid-cols-12 gap-2">
                <div className="col-span-4"><label className="text-[10px] text-slate-400 font-bold block mb-1">EK {currency}</label><div className="relative"><input type="number" step="0.01" value={activeField === 'amE' ? amEK : amEKNum.toFixed(2)} onFocus={(e) => handleFocus(e, 'amE')} onKeyDown={handleKeyDown} onBlur={handleBlur} onChange={(e) => setAmEK(e.target.value as any)} className="noble-input font-bold text-left" /><span className="unit-label">{currency}</span></div></div>
                <div className="col-span-5">
                  <label className="text-[10px] text-slate-400 font-bold block mb-1 text-center">{amMode === 'markup' ? 'Aufschlag %' : 'VK Brutto'}</label>
                  <div className="relative text-center">
                    <input type="number" step={amMode === 'markup' ? "0.1" : "0.01"} value={activeField === 'amV' ? amVar : (amMode === 'markup' ? amVarNum.toFixed(1) : amVarNum.toFixed(2))} onFocus={(e) => handleFocus(e, 'amV')} onKeyDown={handleKeyDown} onBlur={handleBlur} onChange={(e) => setAmVar(e.target.value as any)} className="noble-input font-bold text-white text-center pr-10" />
                    <span className="unit-label">{amMode === 'markup' ? '%' : currency}</span>
                  </div>
                </div>
                <div className="col-span-3 text-right"><label className="text-[10px] text-slate-400 font-bold block mb-1 text-center">{amMode === 'markup' ? 'VK Brutto' : 'Marge %'}</label><div className="bg-[#1a1c24] border border-slate-700 rounded h-10 flex items-center justify-center font-bold text-slate-300 text-sm">{amMode === 'markup' ? fmt(amResValue) : amResValue.toFixed(1) + "%"}</div></div>
              </div>
            </div>

            <div className="sys-calc-panel rounded-lg p-3 shadow-xl shrink-0">
              <div className="flex justify-between items-center border-b border-white/10 pb-1 mb-2">
                <h3 className="text-red-600 dark:text-red-400 text-xs font-bold uppercase tracking-widest">🩸 Rabatt-Killer</h3>
                <button onClick={() => setRkLocked(!rkLocked)} className="text-slate-500 hover:text-white transition-colors">{rkLocked ? '🔒' : '🔓'}</button>
              </div>
              <div className="grid grid-cols-2 gap-2 mb-1">
                <div className="relative">
                  <label className="text-[11px] text-slate-400 font-bold block mb-1">Marge {rkLocked && <span className="text-[8px] text-[#E6C299] ml-1">(System)</span>}</label>
                  <div className="relative">
                    <input type="number" step="0.1" value={activeField === 'rkM' ? rkMargin : rkMarginNum.toFixed(1)} onFocus={(e) => handleFocus(e, 'rkM')} onKeyDown={handleKeyDown} onBlur={handleBlur} onChange={(e) => !rkLocked && setRkMargin(e.target.value as any)} disabled={rkLocked} className={`noble-input has-stepper font-bold text-left ${rkLocked ? 'text-[#E6C299]' : 'text-white'}`} />
                    <span className="unit-label with-stepper"> %</span>
                    <div className={`absolute right-1 top-1 bottom-1 w-9 flex flex-col gap-0.5 z-10 ${rkLocked ? 'opacity-20' : 'opacity-100'}`}>
                      <button onClick={() => !rkLocked && setRkMargin(Number((rkMarginNum + 1).toFixed(1)))} className="flex-1 bg-white/10 rounded-sm text-[10px] text-[#E6C299]">▲</button>
                      <button onClick={() => !rkLocked && setRkMargin(Number(Math.max(0, rkMarginNum - 1).toFixed(1)))} className="flex-1 bg-white/10 rounded-sm text-[10px] text-[#E6C299]">▼</button>
                    </div>
                  </div>
                </div>
                <div className="relative"><label className="text-[11px] text-slate-400 font-bold block mb-1">Rabatt</label><div className="relative"><input type="number" step="0.1" value={activeField === 'rkD' ? rkDiscount : rkDiscountNum.toFixed(1)} onFocus={(e) => handleFocus(e, 'rkD')} onKeyDown={handleKeyDown} onBlur={handleBlur} onChange={(e) => setRkDiscount(e.target.value as any)} className="noble-input has-stepper font-bold text-left" /><span className="unit-label with-stepper"> %</span><div className="absolute right-1 top-1 bottom-1 w-9 flex flex-col gap-0.5 z-10"><button onClick={() => setRkDiscount(Number((rkDiscountNum + 1).toFixed(1)))} className="flex-1 bg-white/10 rounded-sm text-[10px] text-[#E6C299]">▲</button><button onClick={() => setRkDiscount(Number(Math.max(0, rkDiscountNum - 1).toFixed(1)))} className="flex-1 bg-white/10 rounded-sm text-[10px] text-[#E6C299]">▼</button></div></div></div>
              </div>
              <div className="bg-black/50 rounded p-3 text-center border border-[#E6C299]/20 mt-1">
                <div className="text-slate-400 text-[11px] uppercase font-bold mb-0.5 text-left flex justify-between">
                  <span>Mehrumsatz-Bedarf:</span>
                  <span className="text-[9px] opacity-50">{calcs.rentabilitaetIst > 0 ? 'BASIS: REALE IST-MARGE' : 'BASIS: SOLL-KALKULATION'}</span>
                </div>
                <div className={`text-xl font-mono font-bold ${rkResValue === 'VERLUST!' ? 'text-red-400' : 'text-[#E6C299]'}`}>{rkResValue}</div>
              </div>
            </div>

            <div className="sys-calc-panel rounded-lg p-3 shadow-xl shrink-0">
              <div className="flex justify-between items-center border-b border-white/10 pb-1 mb-2"><h3 className="text-[#E6C299] text-xs font-bold uppercase tracking-widest">🚀 Zusatz-Booster</h3><button onClick={() => setZbModeYear(!zbModeYear)} className="px-2 py-1 bg-black/40 border border-[#E6C299]/30 rounded text-[10px] font-bold text-[#E6C299] uppercase transition-all"><span>{zbModeYear ? '◀ Jahr ▶' : '◀ Monat ▶'}</span></button></div>
              <div className="grid grid-cols-2 gap-2 mb-1">
                <div className="relative"><label className="text-[11px] text-slate-400 uppercase font-bold block mb-1">Tage/Wo.</label><div className="relative"><input type="number" value={activeField === 'zbD' ? zbDays : zbDaysNum} onFocus={(e) => handleFocus(e, 'zbD')} onKeyDown={handleKeyDown} onBlur={handleBlur} onChange={(e) => setZbDays(e.target.value as any)} className="noble-input has-stepper font-bold text-left" /><span className="unit-label with-stepper">Tage</span><div className="absolute right-1 top-1 bottom-1 w-9 flex flex-col gap-0.5 z-10"><button onClick={() => setZbDays(zbDaysNum + 1)} className="flex-1 bg-white/10 rounded-sm text-[10px] text-[#E6C299]">▲</button><button onClick={() => setZbDays(Math.max(0, zbDaysNum - 1))} className="flex-1 bg-white/10 rounded-sm text-[10px] text-[#E6C299]">▼</button></div></div></div>
                <div className="relative"><label className="text-[11px] text-slate-400 uppercase font-bold block mb-1">Verkäufe</label><div className="relative"><input type="number" value={activeField === 'zbC' ? zbCust : zbCustNum} onFocus={(e) => handleFocus(e, 'zbC')} onKeyDown={handleKeyDown} onBlur={handleBlur} onChange={(e) => setZbCust(e.target.value as any)} className="noble-input has-stepper font-bold text-left" /><span className="unit-label with-stepper">Stk.</span><div className="absolute right-1 top-1 bottom-1 w-9 flex flex-col gap-0.5 z-10"><button onClick={() => setZbCust(zbCustNum + 1)} className="flex-1 bg-white/10 rounded-sm text-[10px] text-[#E6C299]">▲</button><button onClick={() => setZbCust(Math.max(0, zbCustNum - 1))} className="flex-1 bg-white/10 rounded-sm text-[10px] text-[#E6C299]">▼</button></div></div></div>
              </div>
              <div className="mt-2 text-left"><label className="text-[11px] text-slate-400 uppercase font-bold block mb-1">Wert pro Verkauf</label><div className="relative"><input type="number" step="0.01" value={activeField === 'zbU' ? zbUpsell : zbUpsellNum.toFixed(2)} onFocus={(e) => handleFocus(e, 'zbU')} onKeyDown={handleKeyDown} onBlur={handleBlur} onChange={(e) => setZbUpsell(e.target.value as any)} className="noble-input font-bold text-left" /><span className="unit-label">{currency}</span></div></div>
              <div className="bg-black/50 rounded p-3 text-center border border-[#E6C299]/20 mt-1"><div className="text-slate-400 text-[11px] font-bold mb-0.5">Mehrumsatz pro {zbModeYear ? 'Jahr' : 'Monat'}:</div><div className="text-xl font-mono text-[#E6C299] font-bold">{fmt(zbResValue)} {currency}</div></div>
            </div>
          </div>
        </div>
      </div>

      <div className="sys-calc-nav carbon-weave-bg p-2 flex justify-between items-center shrink-0 safe-area-bottom z-50">
        <svg style={{ position: 'absolute', width: 0, height: 0 }}><defs><linearGradient id="iconGradient" x1="0%" y1="0%" x2="0%" y2="100%"><stop offset="0%" style={{ stopColor: '#E6C299', stopOpacity: 1 }} /><stop offset="100%" style={{ stopColor: '#8a6d4b', stopOpacity: 1 }} /></linearGradient></defs></svg>
        <button onClick={() => selectTab('calc')} className={`flex flex-col items-center gap-1.5 p-2 w-14 transition-all ${activeTab === 'calc' ? 'sys-calc-accent nav-button-active' : 'sys-calc-muted'}`}>
          <svg className="nav-icon-3d" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={activeTab === 'calc' ? 'url(#iconGradient)' : 'currentColor'} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2" ry="2"/><line x1="8" y1="6" x2="16" y2="6"/><circle cx="8" cy="14" r="1.5" fill="currentColor"/><circle cx="12" cy="14" r="1.5" fill="currentColor"/><circle cx="16" cy="14" r="1.5" fill="currentColor"/><circle cx="8" cy="18" r="1.5" fill="currentColor"/><circle cx="12" cy="18" r="1.5" fill="currentColor"/><circle cx="16" cy="18" r="1.5" fill="currentColor"/></svg>
          <span className="text-[7px] font-black uppercase tracking-[0.15em] nav-text-shadow">Rechner</span>
        </button>
        <button onClick={() => selectTab('profit')} className={`flex flex-col items-center gap-1.5 p-2 w-14 transition-all ${activeTab === 'profit' ? 'sys-calc-accent nav-button-active' : 'sys-calc-muted'}`}>
          <svg className="nav-icon-3d" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={activeTab === 'profit' ? 'url(#iconGradient)' : 'currentColor'} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/><circle cx="12" cy="12" r="10" strokeOpacity="0.2"/></svg>
          <span className="text-[7px] font-black uppercase tracking-[0.15em] nav-text-shadow">Profit</span>
        </button>
        <button onClick={() => selectTab('hr')} className={`flex flex-col items-center gap-1.5 p-2 w-14 transition-all ${activeTab === 'hr' ? 'sys-calc-accent nav-button-active' : 'sys-calc-muted'}`}>
          <svg className="nav-icon-3d" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={activeTab === 'hr' ? 'url(#iconGradient)' : 'currentColor'} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="8" r="3.5"/><path d="M4 21c0-3 2-5 5-5"/><circle cx="16" cy="10" r="2.5" strokeOpacity="0.6"/><path d="M13 21c0-2 1.5-3.5 3.5-3.5" strokeOpacity="0.6"/></svg>
          <span className="text-[7px] font-black uppercase tracking-[0.15em] nav-text-shadow">Personal</span>
        </button>
        <button onClick={() => selectTab('biz')} className={`flex flex-col items-center gap-1.5 p-2 w-14 transition-all ${activeTab === 'biz' ? 'sys-calc-accent nav-button-active' : 'sys-calc-muted'}`}>
          <svg className="nav-icon-3d" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={activeTab === 'biz' ? 'url(#iconGradient)' : 'currentColor'} strokeWidth="2.5"><path d="M3 3v18h18"/><path d="m19 9-5 5-4-4-3 3" strokeWidth="3"/><path d="m19 9 2-2M19 9h3M19 9v3" strokeWidth="2"/></svg>
          <span className="text-[7px] font-black uppercase tracking-[0.15em] nav-text-shadow">Business</span>
        </button>
      </div>
    </div>
  );
}